import React, {FC, useState} from 'react';
import styled from 'styled-components';
import uuid from 'uuid-random';

import {useLocalize} from '../../../../libs/shared/core/src/helpers/internationalization/internationalizationContext';
import {defineMessage, Trans} from '../../../../libs/shared/core/src/helpers/internationalization/react';
import {DropdownBoxCoordinator} from '../../../../libs/shared/ui/src/components/boxedInputs/dropdownBoxCoordinator';
import {TextAreaBox} from '../../../../libs/shared/ui/src/components/boxedInputs/textAreaBox';
import {TextBox} from '../../../../libs/shared/ui/src/components/boxedInputs/textBox';
import {Button} from '../../../../libs/shared/ui/src/components/buttons/button';
import {ButtonVariantsEnum} from '../../../../libs/shared/ui/src/components/buttons/buttonConstants';
import {ButtonGroup} from '../../../../libs/shared/ui/src/components/buttons/buttonGroup/buttonGroup';
import {ButtonGroupSpacer} from '../../../../libs/shared/ui/src/components/buttons/buttonGroup/buttonGroupSpacer';
import {SimpleDropdown} from '../../../../libs/shared/ui/src/components/dropdowns/simpleDropdown/simpleDropdown';
import {SimpleDropdownItem} from '../../../../libs/shared/ui/src/components/dropdowns/simpleDropdown/simpleDropdownItem';
import {fontSizes, fontWeights} from '../../../../libs/shared/ui/src/styles/fontStyles';
import {flexCenter} from '../../../../libs/shared/ui/src/styles/mixins';
import {
  AnalyticsWidgetsPageWidgetModel,
  AnalyticsWidgetsPageWidgetTypesEnum,
} from '../../../models/api/analyticsWidgetsPageModel';
import {renderReportSidePanelCloseButton} from '../reportSidePanel/reportSidePanelHelpers';
import {renderSupportReportType} from './supportReportConfigurationHelpers';
import {SupportReportConfigurationViewDropdown} from './supportReportConfigurationViewDropdown';

/*
 * Constants.
 */

const availableMetricTypeOptions = [
  AnalyticsWidgetsPageWidgetTypesEnum.RESPONSE,
  AnalyticsWidgetsPageWidgetTypesEnum.FIRST_RESPONSE,
  AnalyticsWidgetsPageWidgetTypesEnum.CSAT,
];

/*
 * Props.
 */

interface SupportReportConfigurationSidePanelProps {
  metric?: AnalyticsWidgetsPageWidgetModel;
  onRequestClose: () => void;
  onSave: (metric: AnalyticsWidgetsPageWidgetModel) => void;
}

/*
 * Style.
 */

const StyledSidePanelContentDiv = styled.div`
  display: flex;
  flex-flow: column;
  grid-column: 2;
  min-height: 0;
  overflow-y: auto;
`;

const StyledHeaderWrapperDiv = styled.div`
  ${flexCenter}
  min-height: 56px;
  padding: 0 16px;
  color: ${(p) => p.theme.greys.shade70};
  border-bottom: 1px solid ${(p) => p.theme.greys.shade30};
  overflow-y: hidden;
  gap: 8px;
`;

const StyledHeaderTitle = styled.div`
  flex: 1;
  font-size: ${fontSizes.large};
  font-weight: ${fontWeights.medium};
`;

const StyledMetricDetailsWrapperDiv = styled.div`
  flex: 1;
  display: flex;
  flex-flow: column;
  gap: 8px;
  padding: 16px;
`;

const StyledButtonGroup = styled(ButtonGroup)`
  padding: 16px;
  border-top: 1px solid ${(p) => p.theme.greys.shade30};
`;

/*
 * Component.
 */

export const SupportReportConfigurationSidePanel: FC<SupportReportConfigurationSidePanelProps> = ({
  metric,
  onRequestClose,
  onSave,
}) => {
  const [selectedViewId, setSelectedViewId] = useState(metric?.viewId);
  const [selectedWidgetType, setSelectedWidgetType] = useState(
    metric?.type || AnalyticsWidgetsPageWidgetTypesEnum.RESPONSE,
  );
  const [title, setTitle] = useState(metric?.title || '');
  const [description, setDescription] = useState(metric?.description || '');

  const localize = useLocalize();

  return (
    <StyledSidePanelContentDiv>
      <StyledHeaderWrapperDiv>
        <StyledHeaderTitle>{renderHeader(false)}</StyledHeaderTitle>
        {renderReportSidePanelCloseButton(onRequestClose)}
      </StyledHeaderWrapperDiv>
      <StyledMetricDetailsWrapperDiv>
        <TextBox
          isRequired
          label={<Trans id="analytics-automation-reports-support-report-title-input">Title</Trans>}
          value={title}
          onChange={setTitle}
          placeholder={localize(
            defineMessage({
              id: 'analytics-automation-reports-support-report-title-input-placeholder',
              message: 'Add metric title',
            }),
          )}
        />
        <TextAreaBox
          label={
            <Trans id="analytics-automation-reports-support-report-description-input">Description</Trans>
          }
          value={description}
          onChange={setDescription}
          shouldAllowResize
        />
        <DropdownBoxCoordinator
          value={renderSupportReportType(selectedWidgetType)}
          label={
            <Trans id="analytics-automation-reports-support-report-metric-type-input">Metric type</Trans>
          }
          isRequired
          noMargin
          renderDropdown={(popoverProps) => (
            <SimpleDropdown {...popoverProps} width={300}>
              {availableMetricTypeOptions.map((type) => (
                <SimpleDropdownItem key={type} onSelect={() => setSelectedWidgetType(type)}>
                  {renderSupportReportType(type)}
                </SimpleDropdownItem>
              ))}
            </SimpleDropdown>
          )}
        />
        <SupportReportConfigurationViewDropdown
          selectedViewId={selectedViewId}
          onViewSelect={(viewId) => setSelectedViewId(viewId)}
        />
      </StyledMetricDetailsWrapperDiv>
      <StyledButtonGroup>
        <ButtonGroupSpacer />
        <Button onClick={onRequestClose}>
          <Trans id="analytics-automation-reports-support-report-cancel-button">Cancel</Trans>
        </Button>
        <Button
          variant={ButtonVariantsEnum.PRIMARY}
          isDisabled={!title.trim() || !selectedViewId}
          onClick={() => {
            if (!selectedViewId) {
              return;
            }
            onSave({
              title,
              description,
              type: selectedWidgetType,
              viewId: selectedViewId,
              uuid: uuid(),
            });
            onRequestClose();
          }}
        >
          <Trans id="analytics-automation-reports-support-report-save-button">Save</Trans>
        </Button>
      </StyledButtonGroup>
    </StyledSidePanelContentDiv>
  );
};

/*
 * Helpers.
 */

function renderHeader(isEditing: boolean) {
  if (isEditing) {
    return <Trans id="analytics-automation-reports-support-report-sidepanel-edit-header">Edit metric</Trans>;
  }
  return <Trans id="analytics-automation-reports-support-report-sidepanel-new-header">Add metric</Trans>;
}
